"use client";

import React from "react";
import Reveal from "@/components/common/Reveal";
import { Gauge, Layers, ShieldCheck, Cpu } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useLanguage } from "@/hooks/useLanguage";
import { pick, type Lang } from "@/lib/i18n";
interface ManufacturingItem {
  id: string;
  icon: React.ComponentType<{
    className?: string;
  }>;
  badge: Record<Lang, string>;
  title: Record<Lang, string>;
  desc: Record<Lang, string>;
}
const fabricationItems: ManufacturingItem[] = [{
  id: "slot-precision",
  icon: Gauge,
  badge: {
    en: "20 µm – 5.0 mm",
    zh: "20 µm – 5.0 mm",
    es: "20 µm – 5.0 mm",
    de: "20 µm – 5,0 mm",
    fr: "20 µm – 5,0 mm",
    pt: "20 µm – 5,0 mm"
  },
  title: {
    en: "Slot width accuracy",
    zh: "缝隙公差精度",
    es: "Precisión de ranura",
    de: "Spaltmaßhaltigkeit",
    fr: "Précision de fente",
    pt: "Precisão dimensional de fenda"
  },
  desc: {
    en: "Automated digital winding controls pitch to maintain tight slot aperture tolerances across the whole screen surface.",
    zh: "自动化数控缠绕系统精确控制螺距，确保整个滤网表面保持严苛一致的微米级开孔公差。",
    es: "El bobinado digital automático mantiene tolerancias estrictas de ranura en toda la superficie de la rejilla.",
    de: "Automatisiertes digitales Wickeln steuert die Steigung für enge Spaltweitentoleranzen über die gesamte Siebfläche.",
    fr: "L'enroulement numérique automatisé contrôle le pas pour maintenir des tolérances de fente rigoureuses.",
    pt: "O enrolamento digital automatizado controla o passo mantendo tolerâncias estreitas em toda a superfície."
  }
}, {
  id: "fusion-weld",
  icon: Layers,
  badge: {
    en: "100% Intersections",
    zh: "全交叉熔焊",
    es: "100% intersecciones",
    de: "Vollverschweißt",
    fr: "100% intersections",
    pt: "100% interseções"
  },
  title: {
    en: "Resistance fusion weld",
    zh: "电阻熔核结合",
    es: "Soldadura por resistencia",
    de: "Widerstands-Schmelzschweißung",
    fr: "Soudage par résistance",
    pt: "Solda por resistência"
  },
  desc: {
    en: "Simultaneous current and high force fuse V-wire to support rods without solder, filler metals, or slag.",
    zh: "在异型丝与纵向支撑杆交叉点同步施加精密电流与顶锻力，实现无填充金属、无焊渣的冶金结合。",
    es: "Corriente y presión simultáneas funden el alambre en V a las varillas de soporte sin metal de aporte ni escoria.",
    de: "Strom und Druck verschmelzen den V-Draht mit den Stützstäben ohne Lot, Zusatzwerkstoff oder Schlacke.",
    fr: "Courant et pression simultanés soudent le fil en V aux tiges supports sans métal d'apport ni laitier.",
    pt: "Corrente e pressão simultâneas fundem o arame em V às barras de suporte sem metal de adição ou escória."
  }
}, {
  id: "structural-rigidity",
  icon: Cpu,
  badge: {
    en: "High Collapse / Burst",
    zh: "高抗压溃 / 爆破",
    es: "Alto colapso / rotura",
    de: "Hohe Druckfestigkeit",
    fr: "Haute résistance à l'écrasement",
    pt: "Alto colapso / ruptura"
  },
  title: {
    en: "Structural rigidity",
    zh: "高刚性承压架构",
    es: "Rigidez estructural",
    de: "Struktursteifigkeit",
    fr: "Rigidité structurelle",
    pt: "Rigidez estrutural"
  },
  desc: {
    en: "Internal or external support ribs and welded reinforcement rings withstand severe hydraulic and mechanical loads.",
    zh: "内部或外部加强筋条与机加工焊接端环，能够承受极端系统水力压差与机械动态载荷。",
    es: "Nervaduras de soporte y anillos mecanizados soportan severas cargas hidráulicas y mecánicas.",
    de: "Innen- oder Außenrippen und massive Endringe widerstehen extremen hydraulischen und mechanischen Lasten.",
    fr: "Nervures de renfort et bagues d'extrémité massives résistant aux fortes sollicitations hydrauliques et mécaniques.",
    pt: "Nervuras de suporte e anéis usinados suportam severas cargas hidráulicas e mecânicas."
  }
}, {
  id: "corrosion-metallurgy",
  icon: ShieldCheck,
  badge: {
    en: "304 / 316L / Duplex",
    zh: "304 / 316L / 双相钢",
    es: "304 / 316L / Dúplex",
    de: "1.4301 / 1.4404 / 2205",
    fr: "304 / 316L / Duplex",
    pt: "304 / 316L / Duplex"
  },
  title: {
    en: "Corrosion-resistant alloys",
    zh: "耐腐蚀合金选型",
    es: "Aleaciones resistentes",
    de: "Korrosionsfeste Legierungen",
    fr: "Alliages résistants",
    pt: "Ligas resistentes à corrosão"
  },
  desc: {
    en: "Selected for aggressive chemicals, sour gas, sea water, and sanitary cleaning cycles in industrial service.",
    zh: "针对强酸碱化学介质、酸性油气、海水淡化及卫生级在线清洗工况提供适配的耐蚀合金材质。",
    es: "Seleccionadas para químicos agresivos, agua marina y ciclos de limpieza sanitaria en servicio industrial.",
    de: "Ausgewählt für aggressive Chemikalien, Meerwasser und hygienische Reinigungszyklen im Industrieeinsatz.",
    fr: "Sélectionnées pour les produits chimiques agressifs, l'eau de mer et les cycles de nettoyage sanitaire.",
    pt: "Selecionadas para químicos agressivos, água do mar e ciclos de limpeza sanitária em serviços industriais."
  }
}];
const mfgCopy = {
  eyebrow: {
    en: "Fabrication Standards",
    zh: "制造工艺标准",
    es: "Normas de fabricación",
    de: "Fertigungsstandards",
    fr: "Standards de fabrication",
    pt: "Padrões de fabricação"
  },
  heading: {
    en: "Precision Resistance-Welded Wedge Wire Fabrication",
    zh: "精密电阻焊接约翰逊楔形滤网制造工艺",
    es: "Fabricación de rejillas de cuña soldadas por resistencia",
    de: "Präzisionsfertigung widerstandsgeschweißter Spaltsiebe",
    fr: "Fabrication de grilles en fil triangulaire soudées par résistance",
    pt: "Fabricação de telas de arame cunha eletrossoldadas com precisão"
  },
  lead: {
    en: "Cold-rolled V-shaped profile wire is helically wound and fusion-welded to longitudinal support rods at every intersection, delivering smooth inward-widening slots with high mechanical rigidity.",
    zh: "冷轧 V 型梯形丝通过数控缠绕在纵向支撑杆上，每个交叉点实施高强度电阻熔焊，形成内宽外窄的平滑自清理缝隙与坚固结构刚性。",
    es: "El alambre en V laminado en frío se enrolla helicoidalmente y se suelda a varillas longitudinales en cada intersección, ofreciendo ranuras continuas con alta rigidez mecánica.",
    de: "Kaltgewalzter V-Profildraht wird schraubenlinienförmig auf Tragstäbe gewickelt und an jedem Kreuzungspunkt schmelzgeschweißt – für kontinuierliche selbstreinigende Spalte.",
    fr: "Le fil en V étiré à froid est enroulé en hélice et soudé sur des tiges longitudinales à chaque intersection, garantissant des fentes évasées régulières et une rigidité élevée.",
    pt: "O arame em V laminado a frio é enrolado em espiral e eletrossoldado a barras longitudinais em cada cruzamento, oferecendo fendas contínuas com alta rigidez mecânica."
  }
};
export default function Manufacturing() {
  const {
    lang
  } = useLanguage();
  return <section id="wedgewiremanufacturingoverview" data-nav="light" className="bg-muted/50 text-foreground border-b border-border mf-section">
      <div className="mf-container">
        <Reveal>
          <div className="max-w-3xl mb-10">
            <div className="inline-flex items-center gap-2 mb-3">
              <Badge variant="outline" className="bg-card text-foreground px-2.5 py-1 mf-caption font-semibold uppercase tracking-wider font-mono">
                {pick(lang, mfgCopy.eyebrow)}
              </Badge>
            </div>
            <h2 className="mf-h2 text-foreground font-default tracking-tight mb-4">
              {pick(lang, mfgCopy.heading)}
            </h2>
            <p className="mf-lead text-muted-foreground leading-relaxed max-w-[65ch]">
              {pick(lang, mfgCopy.lead)}
            </p>
          </div>
        </Reveal>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {fabricationItems.map((item, idx) => {
          const IconComponent = item.icon;
          return <Reveal key={item.id} delay={Math.min(idx * 60, 300)} className="h-full">
                <Card className="h-full rounded-lg border border-border bg-card p-4 sm:p-5 flex flex-col justify-between">
                  <div>
                    <div className="flex items-center justify-between gap-2 mb-3">
                      <span className="inline-flex size-10 items-center justify-center rounded-md border border-border bg-muted text-primary shrink-0">
                        <IconComponent className="size-5" aria-hidden="true" />
                      </span>
                      <Badge variant="outline" className="font-mono text-xs tracking-tight bg-muted text-foreground border-border py-0.5 px-2">
                        {pick(lang, item.badge)}
                      </Badge>
                    </div>

                    <CardHeader className="p-0 mb-2">
                      <CardTitle className="text-base font-semibold text-foreground font-default">
                        {pick(lang, item.title)}
                      </CardTitle>
                    </CardHeader>

                    <CardContent className="p-0">
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {pick(lang, item.desc)}
                      </p>
                    </CardContent>
                  </div>
                </Card>
              </Reveal>;
        })}
        </div>
      </div>
    </section>;
}